import {
  EntitySettingsDto,
  KnownSettingsKind,
  ProjectModuleSettingsDto
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { useMutation, useQueries, useQuery } from 'react-query';
import { useApi } from '../../core/api/api.context';
import { QueryKeys } from '../../core/queries/query-keys.constants';

export function useMutationCreateProjectModule() {
  const api = useApi();

  return useMutation(
    async ({
      projectID,
      settings,
      settingsSchemaID
    }: {
      projectID: string;
      settingsSchemaID: string;
      settings: Record<string, any>;
    }) => {
      try {
        return await api.projectsSettingsController.create(projectID, settingsSchemaID, settings);
      } catch (err: any) {
        if (err.name === 'RestError' && err.response?.status === 201 && err.response.parsedBody) {
          // Workaround: treat this as a success
          return err.response.parsedBody;
        }

        throw err;
      }
    }
  );
}

export function useMutationUpdateProjectModule() {
  const api = useApi();

  return useMutation(
    async ({
      moduleID,
      projectID,
      settings
    }: {
      projectID: string;
      moduleID: string;
      settings: Record<string, any>;
    }) => {
      return await api.projectsSettingsController.update(projectID, moduleID, settings);
    }
  );
}

export function useQueryListParties(projectID?: string, transactionType?: string) {
  const api = useApi();

  return useQuery([QueryKeys.PROJECTS, projectID, QueryKeys.PARTIES, transactionType], async () => {
    if (!projectID || !transactionType) {
      return [];
    }
    return await api.partiesController.list(projectID, transactionType);
  });
}

export function useQueryListAllProjectModules() {
  const api = useApi();

  return useQuery([QueryKeys.PROJECT_MODULES], async () => {
    const result = await api.settingsSchemasController.getAllSettingsSchemas();
    return result.filter((schema) => schema.kind === 'module');
  });
}

export function useQueryGetProjectModules(projectID?: string) {
  const api = useApi();
  return useQuery([QueryKeys.PROJECTS, projectID, QueryKeys.PROJECT_MODULES], async () => {
    if (!projectID) {
      return [];
    }
    const result = await api.projectsSettingsController.findAll(projectID);
    // Filter and cast the result to ProjectModuleSettingsDto for easier consumption
    return result.filter((module) => module.schema.kind === 'module') as unknown as {
      id: string;
      data: ProjectModuleSettingsDto;
      schema: EntitySettingsDto['schema'];
    }[];
  });
}

export function useQueriesGetProjectModules(projectIDs: string[]) {
  const api = useApi();

  return useQueries(
    projectIDs.map((projectID) => ({
      queryKey: [QueryKeys.PROJECTS, projectID, QueryKeys.PROJECT_MODULES],
      queryFn: async () => {
        if (!projectID) {
          return [];
        }
        const result = await api.projectsSettingsController.findAll(projectID, {
          kind: KnownSettingsKind.Module
        });
        return result as unknown as {
          id: string;
          data: ProjectModuleSettingsDto;
          schema: EntitySettingsDto['schema'];
        }[];
      }
    }))
  );
}

export function useQueryGetProjectModuleOptional(projectID?: string, moduleID?: string) {
  const api = useApi();

  return useQuery([QueryKeys.PROJECTS, projectID, QueryKeys.PROJECT_MODULES, moduleID], async () => {
    if (!projectID || !moduleID) {
      return undefined;
    }
    return await api.projectsSettingsController.findOne(projectID, moduleID);
  });
}

export function useMutationDeleteProjectModule() {
  const api = useApi();

  return useMutation(async ({ projectID, moduleID }: { projectID: string; moduleID: string }) => {
    return await api.projectsSettingsController.delete(projectID, moduleID);
  });
}
